import { FC, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { DashboardBudgetResponse } from "@/models/homePage";
import popupService from "@/services/popupService";
import api from "@/services/api";
import { HomePagePeriod } from "@/models/enums";
import homePageStore from "@/stores/homePageStore";

interface IHomePageBudgetChart {
    budgetPeriod: HomePagePeriod;
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Tooltip,
    Legend
);

export const chartOptions = {
    responsive: true,
    plugins: {
        legend: {
            display: false,
        },
        title: {
            display: false,
        },
    },
};

const HomePageBudgetChart: FC<IHomePageBudgetChart> = (props: IHomePageBudgetChart) => {
    const { t } = useTranslation();
    const [budgetData, setBudgetData] = useState<DashboardBudgetResponse>({
        usedPointsAccumulated: null,
        obligoBalancePoints: null,
        usedPoints: null,
        budgetLabels: null,
    });

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
        },
    };

    const labels = budgetData.budgetLabels ?? [];

    const chartData = {
        labels,
        datasets: [
            {
                label: t("homepage.UsedSum"),
                data: budgetData.usedPointsAccumulated,
                borderColor: "#387276",
                backgroundColor: "#387276",
            },
            {
                label: t("homepage.ObligoBalance"),
                data: budgetData.obligoBalancePoints,
                borderColor: "#C03658",
                backgroundColor: "#C03658",
            },
            {
                label: t("homepage.UsedMonthly"),
                data: budgetData.usedPoints,
                borderColor: "#F1B670",
                backgroundColor: "#F1B670",
                tension: 0.4,
            },
        ],
    };

    const startFetching = async () => {
        try {
            popupService.showLoading();
            const response = await api.getBudgetData(props.budgetPeriod);
            setBudgetData(response.data);
            homePageStore.setDashboardBudgetData(response.data);
            popupService.close();
        } catch (err) {
            await popupService.showApiError(err);
            return;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            if (
                homePageStore.dashboardBudgetData.usedPointsAccumulated == null &&
                homePageStore.dashboardBudgetData.usedPoints == null &&
                homePageStore.dashboardBudgetData.obligoBalancePoints == null
            ) {
                startFetching();
            } else {
                setBudgetData(homePageStore.dashboardBudgetData);
            }
        }
        return () => {
            ignore = true;
        };
    }, [props.budgetPeriod]);

    return <Line options={chartOptions} data={chartData} />;
};

export default HomePageBudgetChart;
